import type { ResidenceCrous } from './crous'

type Fiche = NonNullable<ResidenceCrous['fiche']>
// Finition CROUS (20/10/2026) : deux séparations typographiques seulement.
// Les courriels et URL traversent ces normalisations sans changer un caractère.
const JETONS_PROTEGES = /((?:[a-z][a-z\d+.-]*:\/\/|www\.)[^\s<>]+|[^\s<>@"«»]+@[^\s<>@"«»]+)/giu
export const texteCrous = (texte: string) => texte.split(JETONS_PROTEGES)
  .map((partie, i) => i % 2 ? partie : partie
    .replace(/(\p{L})(\d{5})(?!\d)/gu, '$1 $2')
    .replace(/\)(?=\p{L})/gu, ') '))
  .join('')
export const cleTexte = (texte: string) => texte.replace(/\s+/g, ' ').trim()
// Cette clé sert uniquement aux comparaisons d’identité et de contact.
export const cleAdresse = (texte: string) => cleTexte(texteCrous(texte)).normalize('NFD').replace(/\p{M}/gu, '').toLowerCase()
const uniques = (textes: string[]) => textes.filter((t, i) => t.trim() && textes.findIndex(v => cleTexte(v) === cleTexte(t)) === i)

// Seuls les titres explicites du producteur délimitent les rubriques. Les nombres,
// les phrases et les espaces internes sont conservés, hors texteCrous ci-dessus.
const TITRES = /(?:Localisation|Description|Transports?|Moyens d['’]accès|Types de logements?)\s*:/gi

function blocsPublies(texte: string) {
  const titres = [...texte.matchAll(TITRES)]
  const debut = titres[0]?.index ?? texte.length
  return [
    ...(debut ? [{ titre: '', texte: texte.slice(0, debut).trim(), contenu: texte.slice(0, debut).trim() }] : []),
    ...titres.map((t, i) => {
      const fin = titres[i + 1]?.index ?? texte.length
      return { titre: t[0], texte: texte.slice(t.index, fin).trim(), contenu: texte.slice(t.index + t[0].length, fin).trim() }
    }),
  ].filter(b => b.texte)
}

export function presentationCrous(fiche: Fiche | undefined) {
  // Le champ court contient parfois exactement le même texte, aplati sur une ligne.
  const textes = uniques([fiche?.description ?? '', fiche?.description_courte ?? ''].map(t => texteCrous(t).replace(/\r\n?/g, '\n').trim()))
  const blocs = textes.flatMap(blocsPublies)
  // La première occurrence fait foi, quelle que soit sa rubrique. Un titre qui
  // revient plus loin ne doit pas déplacer ses paragraphes dans le texte source.
  const complet: RubriqueCrous[] = []
  const vus = new Set<string>()
  for (const bloc of blocs) {
    // La conversion HTML sépare aussi les paragraphes par un seul saut.
    const lignes = bloc.contenu.split(/\n+/).map(t => t.trim()).filter(t => {
      const cle = cleTexte(t)
      if (!cle || vus.has(cle)) return false
      vus.add(cle)
      return true
    })
    if (!lignes.length) continue
    const titre = bloc.titre.replace(/\s*:$/, '').trim()
    const precedente = complet[complet.length - 1]
    if (precedente && cleTexte(precedente.titre).toLowerCase() === titre.toLowerCase()) precedente.lignes.push(...lignes)
    else complet.push({ titre, lignes })
  }
  const types = complet.filter(b => /^Types de logements?/i.test(b.titre) && b.lignes.length)
  // Sans rubrique fiable « Types de logement », le bloc publié reste entier.
  const estAcces = (b: RubriqueCrous) => /^(Localisation|Transports?|Moyens d['’]accès)/i.test(b.titre)
  const logements = types.length ? complet.filter(b => !estAcces(b)) : complet
  const acces = types.length ? complet.filter(estAcces) : []
  return { complet, logements, acces }
}

export type RubriqueCrous = { titre: string; lignes: string[] }

type Contact = { libelle: string; valeur: string; type: 'telephone' | 'courriel' | 'adresse' }
const LIBELLES_CONTACT = /(Adresse(?:\s+(?:d['’]accueil|accueil))?|T[ée]l[ée]phone|E-?mail|Courriel)\s*:/gi

export function contactsCrous(fiche: Fiche | undefined, adresse = ''): Contact[] {
  if (!fiche) return []
  const texte = texteCrous(fiche.contact ?? '')
  const libelles = [...texte.matchAll(LIBELLES_CONTACT)]
  const debut = libelles[0]?.index ?? texte.length
  const lignes: Contact[] = [
    ...(fiche.telephone ? [{ libelle: 'Téléphone', valeur: fiche.telephone, type: 'telephone' as const }] : []),
    ...(fiche.courriel ? [{ libelle: 'Courriel', valeur: fiche.courriel, type: 'courriel' as const }] : []),
    ...libelles.map((l, i): Contact => ({
      libelle: l[1], valeur: texte.slice(l.index + l[0].length, libelles[i + 1]?.index ?? texte.length).trim(),
      type: /^T[ée]l/i.test(l[1]) ? 'telephone' : /^(E-?mail|Courriel)/i.test(l[1]) ? 'courriel' : 'adresse',
    })),
    ...(texte.slice(0, debut).trim() ? [{ libelle: 'Adresse', valeur: texte.slice(0, debut).trim(), type: 'adresse' as const }] : []),
  ]
  // Deux valeurs différentes restent présentes, même si elles portent le même libellé.
  const cleContact = (c: Contact) => c.type === 'adresse' ? cleAdresse(c.valeur) : cleTexte(c.valeur)
  return lignes.map(l => ({ ...l, valeur: texteCrous(l.valeur), libelle: l.type === 'courriel' ? 'Courriel' : l.libelle }))
    .filter(l => !(l.type === 'adresse' && /^Adresse$/i.test(l.libelle) && adresse && cleAdresse(l.valeur) === cleAdresse(adresse)))
    .filter((l, i, tous) => tous.findIndex(v => v.type === l.type && cleContact(v) === cleContact(l)) === i)
}
